"use client";

import React, { useRef } from "react";
import { cn } from "@/lib/utils";

type TiltCardProps = {
  children: React.ReactNode;
  className?: string;

  // Max tilt rotation in degrees
  maxTilt?: number;

  // Scale on hover
  scale?: number;

  // Perspective depth
  perspective?: number;

  // Animation speed
  transitionSpeed?: number;

  // Enable/disable tilt
  enableTilt?: boolean;

  // Optional glare/shadow effect
  glare?: boolean;
};

/**
 * TiltCard
 *
 * Reusable 3D hover tilt wrapper component.
 *
 * Usage:
 *
 * <TiltCard>
 *   <Card />
 * </TiltCard>
 *
 * Features:
 * - 3D perspective tilt
 * - Mouse-based rotation
 * - Smooth reset animation
 * - Optional glare/shadow
 * - GPU accelerated
 */

function TiltCard({
  children,
  className = "",
  maxTilt = 10,
  scale = 1.03,
  perspective = 1200,
  transitionSpeed = 300,
  enableTilt = true,
  glare = false,
}: TiltCardProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const glowRef = useRef<HTMLDivElement | null>(null);

  const handleMouseMove = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (!enableTilt || !cardRef.current) return;

    const card = cardRef.current;

    const rect = card.getBoundingClientRect();

    // Mouse position inside card
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (glowRef.current) {
      glowRef.current.style.setProperty("--glow-x", `${x}px`);
      glowRef.current.style.setProperty("--glow-y", `${y}px`);
      glowRef.current.style.opacity = "1";
    }

    // Convert mouse position to percentage (-0.5 to 0.5)
    const rotateX = ((y / rect.height - 0.5) * -2 * maxTilt).toFixed(2);

    const rotateY = ((x / rect.width - 0.5) * 2 * maxTilt).toFixed(2);

    card.style.transform = `
      perspective(${perspective}px)
      rotateX(${rotateX}deg)
      rotateY(${rotateY}deg)
      scale(${scale})
    `;

    // Optional shadow movement
    if (glare) {
      const shadowX = (x / rect.width - 0.5) * 30;
      const shadowY = (y / rect.height - 0.5) * 30;

      card.style.boxShadow = `
        ${-shadowX}px ${shadowY}px 40px rgba(0,0,0,0.25)
      `;
    }
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;

    const card = cardRef.current;

    // Reset smoothly
    card.style.transform = `
      perspective(${perspective}px)
      rotateX(0deg)
      rotateY(0deg)
      scale(1)
    `;

    card.style.boxShadow = "0px 0px 0px rgba(0,0,0,0)";

    if (glowRef.current) {
      glowRef.current.style.opacity = "0";
    }
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={cn(
        "relative transform-gpu will-change-transform",
        className,
      )}
      style={{
        transition: `transform ${transitionSpeed}ms ease-out, box-shadow ${transitionSpeed}ms ease-out`,
        transformStyle: "preserve-3d",
      }}
    >
      {children}
      <div
        ref={glowRef}
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-0 z-10",
          "rounded-[28px] sm:rounded-[30px] md:rounded-[33.438px] lg:rounded-[40px] xl:rounded-[45.813px]"
        )}
        style={{
          opacity: 0,
          transition: "opacity 220ms ease-out",
          background:
            "radial-gradient(220px circle at var(--glow-x, 50%) var(--glow-y, 50%), rgba(255, 255, 255, 0.22), rgba(255, 255, 255, 0.1) 34%, rgba(255, 255, 255, 0) 72%)",
          mixBlendMode: "screen",
        }}
      />
    </div>
  );
}

TiltCard.displayName = "TiltCard";

export default TiltCard;